'use client';

import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types';
import { InterfaceChainUtils, parseApiAsset } from '@morpho-org/dapp-core';
import { NetworkAvatar } from '@morpho-org/ui-v6/components/client/NetworkAvatar';
import VaultCell from '@morpho-org/ui-v6/components/client/VaultCell';
import CellTokenPrice from '@morpho-org/ui-v6/components/server/CellTokenPrice';
import { Skeleton } from '@morpho-org/ui-v6/components/server/Skeleton';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { columnOrderingFeature, columnSizingFeature, columnVisibilityFeature, rowSortingFeature, tableFeatures } from '@tanstack/react-table';
import CellNetApy from '@/components/common/Cells/CellNetApy';
import RiskCuratorCell from '@/components/common/Cells/RiskCuratorCell';
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack';
import { APY_DURATION_LABEL } from '@/constants/avgApy';
import { formatTimestampToShortDate, formatVaultAgeDays } from '@/helpers/date';
import type { TableVaultRow } from './TableVault.types';
import { COLUMN_IDS, COLUMN_SIZES, EXPLORE_VAULTS_COLUMN_LABELS } from './config';
export const tableVaultFeatures = tableFeatures({
  columnOrderingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature
});
export type TableVaultFeatures = typeof tableVaultFeatures;
export const vaultColumnsDefs: ColumnDefWithSkeleton<TableVaultFeatures, TableVaultRow>[] = [{
  id: COLUMN_IDS.Network,
  size: COLUMN_SIZES[COLUMN_IDS.Network],
  accessorFn: row => row.network.chainId,
  enableSorting: false,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.Network]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The network of the vault.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    const {
      chainId
    } = row.original.network;
    const chainName = InterfaceChainUtils.getMetadata(chainId).chain.name;
    return <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <NetworkAvatar chainId={chainId} srcAvatarProps={{
            size: 'medium'
          }} />
            </div>
          </TooltipTrigger>
          <TooltipContent>{chainName}</TooltipContent>
        </Tooltip>;
  },
  skeletonCell: <Skeleton className="h-md w-5 rounded-full" />
}, {
  id: COLUMN_IDS.Vault,
  size: COLUMN_SIZES[COLUMN_IDS.Vault],
  accessorFn: ({
    vault
  }) => vault.name,
  enableSorting: true,
  header: () => <span className="text-secondary-foreground">
        {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.Vault]}
      </span>,
  cell: ({
    row
  }) => {
    const columnVault = row.original.vault;
    return <VaultCell address={columnVault.address} name={columnVault.name} logoURI={columnVault.asset?.logoURI} chainId={columnVault.chainId} isVaultV2={columnVault.version === '2.0'} warnings={columnVault.warnings} renderEnd={columnVault.hasUserPosition ? <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-success-foreground size-3 shrink-0 rounded-full" />
                </TooltipTrigger>
                <TooltipContent>You have an active position in this vault.</TooltipContent>
              </Tooltip> : null} />;
  },
  skeletonCell: <Skeleton className="h-[22px] w-[166px]" />
}, {
  id: COLUMN_IDS.Deposits,
  size: COLUMN_SIZES[COLUMN_IDS.Deposits],
  accessorFn: row => row.deposits.valueUsd,
  enableSorting: true,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.Deposits]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total assets deposited in the vault.</TooltipContent>
      </Tooltip>,
  cell: function DepositsCell({
    row
  }) {
    const columnDeposits = row.original.deposits;
    return <CellTokenPrice token={parseApiAsset(columnDeposits.asset)} amount={columnDeposits.amount} value={columnDeposits.valueUsd} />;
  },
  skeletonCell: <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
}, {
  id: COLUMN_IDS.Liquidity,
  size: COLUMN_SIZES[COLUMN_IDS.Liquidity],
  accessorFn: row => row.liquidity.valueUsd,
  enableSorting: true,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.Liquidity]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total available liquidity in the vault.</TooltipContent>
      </Tooltip>,
  cell: function LiquidityCell({
    row
  }) {
    const columnLiquidity = row.original.liquidity;
    return <CellTokenPrice token={parseApiAsset(columnLiquidity.asset)} amount={columnLiquidity.amount} value={columnLiquidity.valueUsd} />;
  },
  skeletonCell: <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
}, {
  id: COLUMN_IDS.Exposure,
  size: COLUMN_SIZES[COLUMN_IDS.Exposure],
  enableSorting: false,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.Exposure]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The collateral asset of each market the vault is lending to.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    const columnExposure = row.original.exposure;
    return <CollateralExposureStack collateralExposures={columnExposure.collateralExposures} maxTokensCount={5} totalAssetsUsd={columnExposure.totalAssetsUsd} />;
  },
  skeletonCell: <Skeleton className="h-md w-[100px]" />
}, {
  id: COLUMN_IDS.Curator,
  size: COLUMN_SIZES[COLUMN_IDS.Curator],
  accessorFn: row => row.curators,
  enableSorting: false,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground ml-[14px]">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.Curator]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Curators configure vaults to allocate liquidity to lending markets, performing actions
          that Vault users could otherwise undertake directly. Their role is limited to technical
          configuration rather than individualised advice.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <RiskCuratorCell curators={row.original.curators} chainId={null} textClassName="text-xs" withLabel={(row.original.curators?.length ?? 0) <= 1} />,
  skeletonCell: <div className="ml-[14px]">
        <Skeleton className="h-[22px] w-[115px]" />
      </div>
}, {
  id: COLUMN_IDS.NetApy,
  size: COLUMN_SIZES[COLUMN_IDS.NetApy],
  accessorFn: row => row.netApy,
  enableSorting: true,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground ml-[14px]">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.NetApy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`${APY_DURATION_LABEL} Native APY + Rewards - Fees`}</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => {
    const {
      rewards,
      performanceFeeBigInt,
      managementFeeBigInt,
      asset,
      apy,
      netApy,
      vault,
      network
    } = row.original;
    return <CellNetApy rewards={rewards} netApy={netApy} apy={apy} performanceFee={performanceFeeBigInt} managementFee={managementFeeBigInt} variant="vault" points={[]} asset={parseApiAsset(asset)} maxApy={row.original.maxApy} maxRate={row.original.maxApy === 0 ? 0n : null} isRateCapped={row.original.isRateCapped} includesDonations={row.original.includesDonations} apyBreakdown={row.original.apyBreakdown} merklContext={{
      kind: 'vault',
      chainId: network.chainId,
      identifier: vault.address
    }} />;
  },
  skeletonCell: <div className="ml-[14px]">
        <Skeleton className="h-md w-8" />
      </div>
}, {
  id: COLUMN_IDS.PerformanceFee,
  size: COLUMN_SIZES[COLUMN_IDS.PerformanceFee],
  accessorFn: row => row.performanceFee,
  enableSorting: true,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.PerformanceFee]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The percentage fee charged on interest earned by the vault.</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <span className="text-foreground text-xs">
        {formatters.percent.unit('%').trailingZero(false).default('-').of(row.original.performanceFee)}
      </span>,
  skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[26px]" />
}, {
  id: COLUMN_IDS.ManagementFee,
  size: COLUMN_SIZES[COLUMN_IDS.ManagementFee],
  accessorFn: row => row.managementFee,
  enableSorting: true,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.ManagementFee]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The management fee is a continuous fee charged on the vault&apos;s total assets under
          management (AUM), providing a steady revenue stream for the vault&apos;s managers.
        </TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <span className="text-foreground text-xs">
        {row.original.managementFee == null ? '-' : formatters.percent.digits(2).unit('%').trailingZero(false).of(row.original.managementFee)}
      </span>,
  skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[26px]" />
}, {
  id: COLUMN_IDS.VaultAge,
  size: COLUMN_SIZES[COLUMN_IDS.VaultAge],
  accessorFn: row => row.creationTimestamp,
  enableSorting: true,
  header: () => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {EXPLORE_VAULTS_COLUMN_LABELS[COLUMN_IDS.VaultAge]}
          </span>
        </TooltipTrigger>
        <TooltipContent>Date this vault was deployed on chain</TooltipContent>
      </Tooltip>,
  cell: ({
    row
  }) => <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-foreground text-xs">
            {formatTimestampToShortDate(row.original.creationTimestamp)}
          </span>
        </TooltipTrigger>
        <TooltipContent>{formatVaultAgeDays(row.original.creationTimestamp)}</TooltipContent>
      </Tooltip>,
  skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[40px]" />
}];